import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExerciseType } from '@org/model';
import { cn } from '../../../../utils';
import { RotateCcw } from 'lucide-react';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { AnswerCorrection, WorkbookPanel, ZineButton } from '../../zine';
import { zineColors, zineFonts, zinePairColorFor, type ZinePairColor } from '../../zine/zine-theme';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  useAnswerCorrectionLabels,
  WorkbookVerdict,
} from './zine-workbook';

interface ScrambleExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

interface SentenceData {
  sentenceId: string;
  correctSentence: string;
  order: number;
  correction?: string;
}

type WordEvaluation = { isCorrect: boolean } | null | undefined;

/** Zine chip styling shared by word cards and answer slots. */
const chipBase =
  'relative inline-flex min-h-[38px] min-w-[52px] items-center justify-center rounded-[4px] border-[1.5px] px-3 py-1.5 text-[.92rem] transition focus:outline-none focus-visible:ring-2';

interface WordCardProps {
  word: string;
  color: ZinePairColor;
  isInAnswer: boolean;
  isReadOnly: boolean;
  onClick: () => void;
}

function WordCard({ word, color, isInAnswer, isReadOnly, onClick }: WordCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly || isInAnswer}
      className={cn(
        chipBase,
        isInAnswer
          ? 'cursor-not-allowed opacity-40'
          : !isReadOnly && 'cursor-pointer hover:brightness-95 active:scale-[0.98]',
        isReadOnly && 'cursor-default',
      )}
      style={{ background: color.soft, borderColor: color.solid, color: zineColors.ink }}
      aria-label={`Word: ${word}`}
    >
      <span className="font-medium">{word}</span>
    </button>
  );
}

interface AnswerSlotProps {
  word: string | null;
  wordColor: ZinePairColor | null;
  position: number;
  evaluation?: WordEvaluation;
  isReadOnly: boolean;
  onClick: () => void;
}

function AnswerSlot({ word, wordColor, position, evaluation, isReadOnly, onClick }: AnswerSlotProps) {
  // A placed word keeps the color of its word-bank card so students can
  // track where each word went; grading overrides with good/red.
  const filledStyle = evaluation
    ? evaluation.isCorrect
      ? { background: zineColors.goodSoft, borderColor: zineColors.good, color: zineColors.good }
      : { background: zineColors.baby, borderColor: zineColors.redApplied, color: zineColors.redApplied }
    : word && wordColor
      ? { background: wordColor.soft, borderColor: wordColor.solid, color: zineColors.ink }
      : { background: 'rgba(255,255,255,.6)', borderColor: zineColors.line, color: zineColors.warmGray };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isReadOnly || !word}
      className={cn(
        chipBase,
        !word && 'border-dashed',
        word && !isReadOnly && 'cursor-pointer hover:opacity-80 active:scale-[0.98]',
        isReadOnly && 'cursor-default',
      )}
      style={filledStyle}
      aria-label={word ? `Remove word: ${word}` : `Empty slot ${position + 1}`}
    >
      <span
        className="absolute -top-2 -left-2 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
        style={{ background: zineColors.ink, fontFamily: zineFonts.cond }}
      >
        {position + 1}
      </span>
      <span className="font-medium">{word || '___'}</span>
    </button>
  );
}

interface SentenceScrambleProps {
  sentence: SentenceData;
  sentenceIndex: number;
  totalSentences: number;
  userAnswer: string[];
  scrambledWords: { word: string; originalIndex: number }[];
  evaluation?: {
    wordEvaluations: Array<{
      position: number;
      expectedWord: string;
      providedWord: string;
      isCorrect: boolean;
    }>;
    isFullyCorrect: boolean;
  } | null;
  isReadOnly: boolean;
  onWordSelect: (word: string) => void;
  onWordRemove: (position: number) => void;
  onReset: () => void;
}

function SentenceScramble({
  sentence,
  sentenceIndex,
  totalSentences,
  userAnswer,
  scrambledWords,
  evaluation,
  isReadOnly,
  onWordSelect,
  onWordRemove,
  onReset,
}: SentenceScrambleProps) {
  const { t } = useTranslation('p2e');
  const correctionLabels = useAnswerCorrectionLabels();
  const wordsInAnswer = new Set(userAnswer);
  const correctWords = sentence.correctSentence.trim().split(/\s+/);

  // Word → color of its word-bank card (duplicate words share a hue).
  const wordColorMap = useMemo(() => {
    const map = new Map<string, ZinePairColor>();
    for (const w of scrambledWords) {
      if (!map.has(w.word)) {
        map.set(w.word, zinePairColorFor(w.originalIndex));
      }
    }
    return map;
  }, [scrambledWords]);

  // Build evaluation map for answer slots
  const evaluationMap = useMemo(() => {
    if (!evaluation?.wordEvaluations) return new Map<number, { isCorrect: boolean }>();
    const map = new Map<number, { isCorrect: boolean }>();
    for (const wordEval of evaluation.wordEvaluations) {
      map.set(wordEval.position, { isCorrect: wordEval.isCorrect });
    }
    return map;
  }, [evaluation]);

  return (
    <div className="space-y-3">
      {/* Sentence header */}
      {totalSentences > 1 && (
        <div className="flex items-center justify-between">
          <h4
            className="m-0 text-[.82rem] font-semibold uppercase tracking-[.12em]"
            style={{ fontFamily: zineFonts.cond, color: zineColors.warmGray }}
          >
            {t('zine.workbook.sentence', { n: sentenceIndex + 1 })}
          </h4>
          {!isReadOnly && userAnswer.length > 0 && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex items-center gap-1 rounded-full border bg-white px-3 py-1 text-[.72rem] font-semibold transition hover:opacity-80"
              style={{ borderColor: zineColors.line, color: zineColors.warmGray }}
            >
              <RotateCcw className="h-3 w-3" />
              {t('zine.workbook.reset')}
            </button>
          )}
        </div>
      )}

      {/* Word bank - scrambled words */}
      <div
        className="rounded-[4px] border p-3"
        style={{ background: '#fff', borderColor: zineColors.line }}
      >
        <p
          className="mb-2 mt-0 text-center text-[.72rem] font-semibold uppercase tracking-[.18em]"
          style={{ fontFamily: zineFonts.cond, color: zineColors.warmGray }}
        >
          {t('zine.workbook.wordBank')}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {scrambledWords.map((wordData, idx) => {
            const isInAnswer = wordsInAnswer.has(wordData.word);
            return (
              <WordCard
                key={`${wordData.word}-${idx}`}
                word={wordData.word}
                color={zinePairColorFor(wordData.originalIndex)}
                isInAnswer={isInAnswer}
                isReadOnly={isReadOnly}
                onClick={() => {
                  if (!isInAnswer) {
                    onWordSelect(wordData.word);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Answer area */}
      <div
        className="rounded-[4px] border border-dashed p-3"
        style={{ background: 'rgba(255,255,255,.6)', borderColor: zineColors.line }}
      >
        <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
          <p
            className="m-0 text-center text-[.72rem] font-semibold uppercase tracking-[.18em]"
            style={{ fontFamily: zineFonts.cond, color: zineColors.warmGray }}
          >
            {t('zine.workbook.yourSentence')}
          </p>
          {evaluation && (
            <AnswerCorrection
              state={
                evaluation.isFullyCorrect ? 'right' : userAnswer.length === 0 ? 'empty' : 'wrong'
              }
              labels={correctionLabels}
              yourAnswer={userAnswer.join(' ')}
              correctAnswer={sentence.correctSentence}
              reason={sentence.correction}
            />
          )}
        </div>
        <div className="flex min-h-[50px] flex-wrap justify-center gap-2">
          {correctWords.map((_, position) => {
            const word = userAnswer[position] || null;
            const positionEval = evaluationMap.get(position);

            return (
              <AnswerSlot
                key={position}
                word={word}
                wordColor={word ? wordColorMap.get(word) ?? null : null}
                position={position}
                evaluation={positionEval}
                isReadOnly={isReadOnly}
                onClick={() => {
                  if (word) {
                    onWordRemove(position);
                  }
                }}
              />
            );
          })}
        </div>
      </div>

    </div>
  );
}

/**
 * Shuffle array using Fisher-Yates algorithm with a seed
 */
function shuffleWithSeed<T>(array: T[], seed: number): T[] {
  const result = [...array];
  let currentIndex = result.length;
  let randomValue = seed;

  // Simple seeded random number generator
  const seededRandom = () => {
    randomValue = (randomValue * 9301 + 49297) % 233280;
    return randomValue / 233280;
  };

  while (currentIndex !== 0) {
    const randomIndex = Math.floor(seededRandom() * currentIndex);
    currentIndex--;
    [result[currentIndex], result[randomIndex]] = [result[randomIndex], result[currentIndex]];
  }

  return result;
}

export function ScrambleExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: ScrambleExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const rawSentences = content.sentences ?? [];
  const sentences: SentenceData[] = useMemo(() => {
    return rawSentences.map((sentence, index) => ({
      sentenceId: sentence.sentenceId ?? `sentence-${index}`,
      correctSentence: sentence.correctSentence ?? '',
      order: sentence.order ?? index,
      correction: sentence.correction ?? undefined,
    }));
  }, [rawSentences]);

  const lastSubmission = useMemo(() => {
    const submission = content.lastSubmission;
    if (submission?.type === ExerciseType.Scramble) {
      return submission;
    }
    return null;
  }, [content.lastSubmission]);

  const lastEvaluationDetails = useMemo(() => {
    const evaluation = (content.lastEvaluation as any) ?? null;
    const autoDetails = (evaluation?.auto as any)?.details ?? evaluation?.details;
    return autoDetails?.kind === 'Scramble' ? autoDetails : null;
  }, [content.lastEvaluation]);

  // Initialize answers from last submission
  const [answers, setAnswers] = useState<string[][]>(() => {
    if (lastSubmission?.answers) {
      return lastSubmission.answers;
    }
    return sentences.map(() => []);
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Sentences edited since the last check: only these hide their correction,
  // the others keep showing what the student got right or wrong.
  const [modifiedSinceEvaluation, setModifiedSinceEvaluation] = useState<Set<number>>(
    () => new Set(),
  );

  // Generate scrambled words for each sentence
  const scrambledWordsBySentence = useMemo(() => {
    return sentences.map((sentence, sentenceIndex) => {
      const words = sentence.correctSentence.trim().split(/\s+/).filter(w => w.length > 0);
      const wordsWithMeta = words.map((word, originalIndex) => ({
        word,
        originalIndex,
      }));

      // Use sentence ID or index as seed for consistent shuffling
      const seed = sentence.sentenceId?.charCodeAt(0) ?? sentenceIndex + 42;
      return shuffleWithSeed(wordsWithMeta, seed);
    });
  }, [sentences]);

  const markModified = useCallback(
    (sentenceIndex: number) => {
      if (lastEvaluationDetails && !modifiedSinceEvaluation.has(sentenceIndex)) {
        setModifiedSinceEvaluation((prev) => new Set(prev).add(sentenceIndex));
      }
    },
    [lastEvaluationDetails, modifiedSinceEvaluation],
  );

  const updateSentenceAnswer = useCallback(
    (sentenceIndex: number, updater: (current: string[]) => string[]) => {
      if (isReadOnly) return;
      markModified(sentenceIndex);
      setAnswers((prev) => {
        const newAnswers = [...prev];
        newAnswers[sentenceIndex] = updater([...(newAnswers[sentenceIndex] || [])]);
        return newAnswers;
      });
    },
    [isReadOnly, markModified]
  );

  const handleWordSelect = useCallback(
    (sentenceIndex: number, word: string) => {
      updateSentenceAnswer(sentenceIndex, (current) => {
        if (!current.includes(word)) current.push(word);
        return current;
      });
    },
    [updateSentenceAnswer]
  );

  const handleWordRemove = useCallback(
    (sentenceIndex: number, position: number) => {
      updateSentenceAnswer(sentenceIndex, (current) => {
        current.splice(position, 1);
        return current;
      });
    },
    [updateSentenceAnswer]
  );

  const handleReset = useCallback(
    (sentenceIndex: number) => {
      updateSentenceAnswer(sentenceIndex, () => []);
    },
    [updateSentenceAnswer]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);

      await onExerciseSubmit(content, {
        type: ExerciseType.Scramble,
        answers,
      });

      setModifiedSinceEvaluation(new Set());
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if any sentence has at least one word
  const hasAnyAnswer = answers.some((a) => a.length > 0);
  // Check if all sentences are complete
  const allComplete = sentences.every((sentence, idx) => {
    const words = sentence.correctSentence.trim().split(/\s+/).filter(w => w.length > 0);
    return (answers[idx]?.length ?? 0) === words.length;
  });

  // Get evaluation for a specific sentence
  const getEvaluationForSentence = (sentenceIndex: number) => {
    if (modifiedSinceEvaluation.has(sentenceIndex) || !lastEvaluationDetails?.sentenceEvaluations) {
      return null;
    }
    return lastEvaluationDetails.sentenceEvaluations[sentenceIndex] ?? null;
  };

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.autoChecked')}
        title={content.title ? renderInlineMarkdown(content.title, 'scramble-title') : undefined}
        hint={t('zine.workbook.buildSentences')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!hasAnyAnswer || isSubmitting}>
                {isSubmitting
                  ? t('zine.workbook.submitting')
                  : allComplete
                    ? t('zine.workbook.checkAnswers')
                    : t('zine.workbook.submitPartial')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />

        {/* Sentences */}
        <div className="mb-4 space-y-5">
          {sentences.map((sentence, sentenceIndex) => (
            <SentenceScramble
              key={sentence.sentenceId}
              sentence={sentence}
              sentenceIndex={sentenceIndex}
              totalSentences={sentences.length}
              userAnswer={answers[sentenceIndex] || []}
              scrambledWords={scrambledWordsBySentence[sentenceIndex]}
              evaluation={getEvaluationForSentence(sentenceIndex)}
              isReadOnly={isReadOnly}
              onWordSelect={(word) => handleWordSelect(sentenceIndex, word)}
              onWordRemove={(position) => handleWordRemove(sentenceIndex, position)}
              onReset={() => handleReset(sentenceIndex)}
            />
          ))}
        </div>
      </WorkbookPanel>
    </form>
  );
}
